define([
	"dojo/_base/declare",
	"dojo/dom-construct",
	"underscore",
	"csi",
	"csi/heading/_base",
	"csi/heading/AggregateAttribute",
	"csi/heading/Container",
	"csi/heading/RendererStoreMixin",
	"csi/html",
	"csi/locale",
	"csi/middleware/common",
	"csi/middleware/inline/InlinePopupService",
	"csi/store/DelayedRequest",
], function (declare, domConstruct, _, csi, csiHeading, AggregateAttribute, Container, RendererStoreMixin, html, locale,
	{ convertNamesStoreDataToComponentItems }, { CONTROL_TYPE_MULTI_SELECT }, { DelayedRequest },) {

	const MAX_RENDER_LIMIT = 1000;

	return declare([Container, RendererStoreMixin], {
		// Boolean: ref containers should be sorted
		bSort: true,

		/** @property {number} renderLimit - if non-zero, limit the number of items rendered in the view; the remaining will be rendered in the tooltip */
		renderLimit: 0,

		// String: if sorting, indicates attribute to sort by -- only valid when not using sub-heading
		//_sortAttribute: null,

		canBeLarge: function (/*Context*/ctx, /*Heading*/h) {
			// summary: if any sub-headings can be large, then so can combination
			var sh = h.m_subheadings ? h.m_subheadings[0] : null;
			var n = 1;

			if (sh && (sh.m_separator == null)) {
				var ren = csiHeading.getHeadingRenderer(null, sh);
				if (ren) {
					// NOTE: context is not updated with one of referenced nodes; hopefully that will not cause problems,
					// as the OOTB renderers do not look at the context node, but use other logic to determine how large it can be
					n = ren.canBeLarge(ctx, sh);
				}
			}

			return Math.max(n, this.inherited(arguments));
		},

		canCopy: function (/*Context*/ctxCopy, /*Heading*/h) {
			// summary: only allow copying of ref if there is a key to the store so we can verify
			// if pasting to a cell with the same store
			if (this.inherited(arguments)) {
				var key = this.getStoreKey(ctxCopy, h);
				if (key) {
					ctxCopy.__copyKey = key;
					return true;
				}
			}

			return false;
		},

		canPaste: function (/*Context*/ctxCopy, /*Context*/ctxPaste, /*Heading*/h) {
			// summary: check store key against copied node's store key
			var key = this.getStoreKey(ctxPaste, h);
			return this.inherited(arguments) && (key == ctxCopy.__copyKey);
		},

		formatValueForFiltering: function (/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// summary: formatted value is same as filter value
			return value;
		},

		toHtmlStatic: function (/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData) {
			// summary: render value for attribute in HTML
			var sh = this._getSubHeadingToRender(h);
			var sep = this._getSeparator(h);
			var limitParams = {};
			var activeSep;

			if (sh) {
				// render each child using attribute's normal renderer - editing not currently supported
				this._iterateNodes(ctx, h, function (ctx, node, index, sh) {
					csiHeading.applyHeadingRenderer(ctx, sh, function (renBase, hBase) {
						if (renBase) {
							ctx.setReadOnly(true);
							renBase._toHtmlAsSub(ctx, hBase, refNode, activeSep);
							activeSep = sep;
						}
					});
				}, true, limitParams);
			} else {
				// render name for each referenced node
				this._iterateNodes(ctx, h, function (ctx, node, index, sh) {
					if (activeSep) html.textSeparator(activeSep, refNode);
					this._renderNode(ctx, h, refNode, node);
					activeSep = sep;
				}, true, limitParams);
			}

			// render more link if some omitted due to render limit
			const more = limitParams.more;
			if (more > 0) {
				if (activeSep) {
					html.textSeparator(activeSep, refNode);
				}

				html.a({
					innerText: locale.resource("MoreListResults", [more]),
					onclick: html.stopPropagation
				}, refNode);
			}

			return (activeSep === sep);
		},

		/**
		 * @override
		 * Render results again if the full number was limited
		 */
		toTooltip: function (ctx, h, tooltip) {
			const renderLimit = this._getRenderLimit();

			this.inherited(arguments);

			if (renderLimit !== MAX_RENDER_LIMIT) {
				const nodes = _.compact(this.getReferencedNodes(ctx, h) || []);

				if (nodes.length > renderLimit) {
					// render all content
					const domNode = tooltip.addLabeledContent(h.m_name);
					const tempLimit = this.renderLimit;
					this.renderLimit = 0;
					this.toHtmlStatic(ctx, h, domNode);
					this.renderLimit = tempLimit;
				}
			}
		},

		getNodeDisplayName: function (/*Context*/ctx, /*Node*/node) {
			// summary: get the node display name. Allows to override display names in derived classes.
			return locale.getNodeReferenceName(node, ctx.cache, ctx.locale);
		},

		/**
		 * @override
		 * provide specific operators for Ref Container
		 */
		getQuerySpec(ctx, h, spec) {
			var qs = this.inherited(arguments);
			if (qs) {
				qs.ops = csiHeading.QUERY_OPS.refcontainer;
			}

			return qs;
		},

		getReferencedNodes: function (/*Context*/ctx, /*Heading*/h) {
			// summary: get nodes referenced by the heading attribute
			return ctx.cache.getChildren(ctx.getNode(), h.m_id, !ctx.bPathsQueried, ctx.bPathsQueried);
		},

		/**
		 * Return limit for number of nodes to process
		 * @return {number} 
		 */
		_getRenderLimit: function () {
			return (this.renderLimit > 0) ? Math.min(this.renderLimit, MAX_RENDER_LIMIT) : MAX_RENDER_LIMIT;
		},

		/**
		 * @override
		 * Prevent action if the URL is valid, but user does not have access to node
		 */
		_getRenderAction(ctx, h) {
			const urls = this._getValue(ctx, h);
			const allHaveAccess = _.every(urls, ctx.cache.get, ctx.cache);
			return allHaveAccess ? this.inherited(arguments) : null;
		},

		/**
		 * @override
		 * Return aggregate action if not configured with Heading
		 */
		_getRenderActionForHeading(ctx, h) {
			const a = new AggregateAttribute();
			a.aggregateId = h.m_id;

			// for the new aggregate store architecture, it is assumed that any Request must accept all added and removed URLs (rather than using one request for each)
			a.bOneRequest = !!h.m_action?.m_request;
			return a;
		},

		_getSortValues: function (/*Context*/ctx, /*Heading*/h, /*Any*/value, /*String[]*/textValues) {
			// summary: return null as _getTextValues returns sorted list
			return null;
		},

		_getSubHeadingToRender: function (/*Heading*/h) {
			// summary: if sub-heading without separator, then render value of sub-heading for each referenced node rather than Node Name
			var subs = h.m_subheadings;
			return (subs && (subs[0].m_separator == null)) ? subs[0] : null;
		},

		_getTextValues: function (/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// summary: return array of formatted values for container values
			return this._iterateNodes(ctx, h, this._toNodeValue, true);
		},

		/**
		 * Iterate through referenced nodes and apply given function to retrieve list of values
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {Context} f
		 * @param {boolean} [bSortNodes]
		 * @param {Object} [limitParams] - if provided, then apply renderLimit and track how many more nodes exist
		 */
		_iterateNodes: function (ctx, h, f, bSortNodes, limitParams) {
			var nodes = this.getReferencedNodes(ctx, h);
			var id = this._sortAttribute;
			var sh = this._getSubHeadingToRender(h);
			var sortValues;
			var values;

			if (!nodes) {
				return null;
			}

			nodes = _.compact(nodes);
			if (!nodes.length) {
				return [];
			}

			// sort referenced nodes if so instructed and container is not ordered
			if (bSortNodes && this.bSort) {
				if (id) {
					sortValues = _.map(nodes, function (node) { return node[id]; });
				} else {
					sortValues = this._iterateNodes(ctx, h, this._toNodeSortValue);
				}

				nodes = csi.sortByArray(nodes, sortValues);
			}

			var limit = limitParams ? this._getRenderLimit() : MAX_RENDER_LIMIT;

			if (sh) {
				// apply function to each referenced node with sub-heading
				values = [];

				ctx.forEach(nodes, function (ctx, i) {
					if (--limit >= 0) {
						values[i] = f.call(this, ctx, ctx.getNode(), i, sh);
					}
				}, this);
			} else {
				// apply function to process each node - node not applied to context for faster performance
				values = _.map(nodes, function (node, i) {
					return (--limit >= 0) ? f.call(this, ctx, node, i) : null;
				}, this);
			}

			// fill in number of additional items over limit
			if (limitParams) {
				limitParams.more = (limit < 0) ? -limit : 0;
			}

			return values;
		},

		_renderNode: function (/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*Node*/node) {
			// summary: create anchor for given node; same as Ref renderer, except ensure that each node is wrapped in span even when not a link (anchor)
			var nodeLink = this._getLinkNode(ctx, h, node);
			var anchor = this._toHtmlAnchor(ctx, h, refNode, nodeLink);
			var value = this.getNodeDisplayName(ctx, node);
			refNode = anchor || domConstruct.create("span", null, refNode);
			html.text(refNode, value);
			return refNode;
		},

		_toFilterValue: function (/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// summary: if empty array then return null since it will not match any filter values (other than blank)
			var values = this._iterateNodes(ctx, h, this._toNodeFilterValue);
			if (values) {
				// remove null/undefined and duplicate values
				values = _.uniq(values);
				values = values.filter(v => (v != null));
				return values;
			}

			return null;
		},

		_toNodeFilterValue: function (/*Context*/ctx, /*Node*/node, /*Number*/index, /*Heading?*/sh) {
			// summary: return filter value for given node
			var value;

			if (sh) {
				csiHeading.applyHeadingRenderer(ctx, sh, function (renBase, hBase) {
					value = renBase ? renBase.toFilterValue(ctx, hBase) : null;
				});
			} else {
				value = locale.getNodeStoredName(node, ctx.cache, ctx.locale);
			}

			return value;
		},

		_toNodeSortValue: function (/*Context*/ctx, /*Node*/node, /*Number*/index, /*Heading?*/sh) {
			// summary: return sort value for given node
			var value;

			if (sh) {
				csiHeading.applyHeadingRenderer(ctx, sh, function (renBase, hBase) {
					value = renBase ? renBase.toSortValue(ctx, hBase) : null;
				});
			} else {
				value = locale.getNodeStoredName(node, ctx.cache, ctx.locale);
			}

			return value;
		},

		_toNodeValue: function (/*Context*/ctx, /*Node*/node, /*Number*/index, /*Heading?*/sh) {
			// summary: return value for given node
			var value;

			if (sh) {
				csiHeading.applyHeadingRenderer(ctx, sh, function (renBase, hBase) {
					value = renBase ? renBase.toValue(ctx, hBase) : null;
				});
			} else {
				value = this.getNodeDisplayName(ctx, node);
			}

			return value;
		},

		/** React */

		/**
		 * @override
		 */
		getReactControlOptions: async function (store, controller, query, queryOptions) {
			const data = await store.query(query, queryOptions);
			const options = convertNamesStoreDataToComponentItems(data, store);
			const { oldValue } = controller;
			const value = _.isEmpty(oldValue) ? [] : this.getValueOptions(options, oldValue);
			const selectActions = controller.getSelectActions(controller._actionViewParameters, store);
			return {
				allowCustomOptions: this.reactAllowCustomOptions,
				placeholderRes: this.reactPlaceholderRes,
				options,
				selectActions, 
				store,
				value,
			};
		},

		/**
		 * @override
		 */
		getReactPlaceholderOptions: function (controller, ctx, h) {
			const { delaySearch } = controller;
			let { oldValue } = controller;
			const store = this.getStore(ctx, h, oldValue);
			const selectActions = controller.getSelectActions(controller._actionViewParameters, store);

			let delayedStore;
			if (delaySearch) {
				delayedStore = new DelayedRequest(store);
			}
			const effectiveStore = delaySearch? delayedStore : store;

			if (_.isEmpty(oldValue)) {
				controller.oldValue = controller.newValue = [];
				return {
					options: [],
					selectActions,
					store: effectiveStore,
					value: null,
				};
			}
			// Force olValue type to array
			if (!Array.isArray(oldValue) && _.isObject(oldValue)) {
				const arr = [];
				Object.keys(oldValue).forEach((e) => arr.push(oldValue[e]))
				controller.oldValue = controller.newValue = oldValue = arr;
			}
			const data = [];
			oldValue.forEach(val => {
				const item = store.getFromMemory(val);
				if (item) {
					data.push(item);
				}
			});
			const options = convertNamesStoreDataToComponentItems(data, store);
			const value = this.getValueOptions(options, oldValue);
			return {
				options,
				selectActions, 
				store: effectiveStore,
				value,
			};
		},

		/**
		 * @override
		 * @param {context} ctx
		 * @return {string}
		 */
		getReactControlType: function (ctx) {
			return CONTROL_TYPE_MULTI_SELECT;
		},

		/** 
		 * return true if react renderer exists for given attribute type and should be used
		 */
		useReactInlineDialog () {
			return true;
		},

	});
});